import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useIAMData } from './hooks';
import * as api from './api';
import type { IamLogContext } from './api';
import { User, UserGroup, PermissionToRemove } from './types';
import { permissionsRecordToPayload, removePermission } from './utils';
import { 
  Tabs, 
  TabsList, 
  TabsTrigger, 
  TabsContent, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter, 
  Button,
  Skeleton,
  SpinnerLoader,
  PageHeader,
  Input,
} from 'lift-ui';
import { AlertCircle, RefreshCw, Search, UserPlus, Users } from 'lucide-react';
import InfoCards from './components/InfoCards';
import ProductsSection from './components/ProductsSection';
import UserManagement from './components/UserManagement';
import GroupManagement from './components/GroupManagement';
import AssignPermissionSheet from './components/sheets/AssignPermissionSheet';
import CreateGroupSheet from './components/sheets/CreateGroupSheet';
import AssignGroupPermissionSheet from './components/sheets/AssignGroupPermissionSheet';
import EditGroupMembersSheet from './components/sheets/AddUserToGroupSheet';
import PromoteToSuperadminSheet from './components/sheets/PromoteToSuperadminSheet';
import RemovePermissionSheet from './components/sheets/RemovePermissionSheet';
import AddProductSheet from './components/sheets/AddProductSheet';
import './iam-support.styles.scss';

const IAMSupport: React.FC = () => {
  // Tab and search states
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  // Get IAM data with search term
  const {
    users,
    usersLoading,
    usersPage,
    usersRowsPerPage,
    usersTotalItems,
    usersTotalPages,
    setUsersPage,
    setUsersRowsPerPage,
    
    groups,
    groupsLoading,
    groupsPage,
    groupsRowsPerPage,
    groupsTotalItems,
    groupsTotalPages,
    setGroupsPage,
    setGroupsRowsPerPage,
    
    products,
    productNames,
    permissionLevels,
    levelNames,
    loading,
    error,
    refetchUsers,
    refetchGroups,
    refetchAll,
    superadminCount,
    superadminCountLoading,
  } = useIAMData(userSearchTerm);

  // Sheet states
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showGroupPermissionDialog, setShowGroupPermissionDialog] = useState(false);
  const [showEditMembersDialog, setShowEditMembersDialog] = useState(false);
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [showRemovePermissionDialog, setShowRemovePermissionDialog] = useState(false);
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [showDeleteGroupDialog, setShowDeleteGroupDialog] = useState(false);
  
  // Selection states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [permissionToRemove, setPermissionToRemove] = useState<PermissionToRemove | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const productNameById = useMemo(() => {
    const map: Record<number, string> = {};
    products.forEach((p) => {
      map[p.product_id] = p.product_name;
    });
    return map;
  }, [products]);

  const userLabelById = useMemo(() => {
    const map: Record<number, string> = {};
    users.forEach((u) => {
      map[u.id] = u.name || u.email || `User #${u.id}`;
    });
    return map;
  }, [users]);

  const buildUserLogContext = useCallback(
    (userId: number): IamLogContext => {
      const user = users.find((u) => u.id === userId);
      return {
        userName: user?.name,
        userEmail: user?.email,
        productNames: productNameById,
        userLabels: userLabelById,
      };
    },
    [users, productNameById, userLabelById]
  );

  const buildGroupLogContext = useCallback(
    (groupId: number): IamLogContext => {
      const group = groups.find((g) => g.id === groupId);
      return {
        groupName: group?.name,
        productNames: productNameById,
        userLabels: userLabelById,
      };
    },
    [groups, productNameById, userLabelById]
  );

  // Handlers
  const handleAssignAllPermissions = useCallback(async (userId: number, permissions: Record<string, string>) => {
    setIsSubmitting(true);
    try {
      const payload = permissionsRecordToPayload(permissions, products);
      await api.setUserPermissions(userId, payload, buildUserLogContext(userId));
      await refetchUsers();
    } catch (err) {
      console.error('Failed to update permissions:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [products, refetchUsers, buildUserLogContext]);

  const handleSaveGroupPermissions = useCallback(async (
    groupId: number,
    permissions: Array<{ productId: number; levelName: string }>
  ) => {
    setIsSubmitting(true);
    try {
      await api.setGroupPermissions(
        groupId,
        permissions,
        buildGroupLogContext(groupId)
      );
      await refetchGroups();
      setShowGroupPermissionDialog(false);
    } catch (err) {
      console.error('Failed to save group permissions:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [refetchGroups, buildGroupLogContext]);

  const handleCreateGroup = useCallback(async (name: string, description?: string) => {
    setIsSubmitting(true);
    try {
      await api.createGroup(name, description);
      await refetchGroups();
    } catch (err) {
      console.error('Failed to create group:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [refetchGroups]);

  const handleSaveGroupMembers = useCallback(async (groupId: number, toAdd: number[], toRemove: number[]) => {
    setIsSubmitting(true);
    try {
      const ctx = buildGroupLogContext(groupId);
      const promises: Promise<any>[] = [];
      if (toAdd.length > 0) promises.push(api.addUsersToGroup(groupId, toAdd, ctx));
      if (toRemove.length > 0) {
        promises.push(api.removeUsersFromGroup(groupId, toRemove, ctx));
      }
      await Promise.all(promises);
      await refetchGroups();
      await refetchUsers();
      setShowEditMembersDialog(false);
    } catch (err) {
      console.error('Failed to update group members:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [refetchGroups, refetchUsers, buildGroupLogContext]);

  const handlePromoteToSuperadmin = useCallback(async (userId: number) => {
    setIsSubmitting(true);
    try {
      await api.promoteToSuperadmin(userId, buildUserLogContext(userId));
      await refetchUsers();
    } catch (err) {
      console.error('Failed to promote to superadmin:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [refetchUsers, buildUserLogContext]);

  const handleRemovePermission = useCallback(async (userId: number, productKey: string) => {
    setIsSubmitting(true);
    try {
      // Get user's current permissions and remove the specified one
      const user = users.find(u => u.id === userId);
      const currentPermissions = user?.permissions || {};
      const updatedPermissions = removePermission(currentPermissions, productKey);
      
      // Convert to API payload
      const payload = permissionsRecordToPayload(updatedPermissions, products);
      
      await api.setUserPermissions(userId, payload, buildUserLogContext(userId));
      await refetchUsers();
    } catch (err) {
      console.error('Failed to remove permission:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [users, products, refetchUsers, buildUserLogContext]);


  const handleAddProduct = useCallback(async (name: string) => {
    // Note: Product creation endpoint not available in current backend
    // This would need to be implemented in the backend
    console.log('Add product:', name);
    // For now, just close the dialog
  }, []);

  const handleDeleteGroup = useCallback((groupId: number) => {
    setGroupToDelete(groupId);
    setShowDeleteGroupDialog(true);
  }, []);

  const confirmDeleteGroup = useCallback(async () => {
    if (groupToDelete !== null) {
      setIsSubmitting(true);
      try {
        await api.deleteGroup(
          groupToDelete,
          buildGroupLogContext(groupToDelete)
        );
        await refetchGroups();
        setShowDeleteGroupDialog(false);
        setGroupToDelete(null);
      } catch (err) {
        console.error('Failed to delete group:', err);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [groupToDelete, refetchGroups, buildGroupLogContext]);

  // Sync selectedGroup with updated groups array when sheets are open
  useEffect(() => {
    if (selectedGroup) {
      const updatedGroup = groups.find(g => g.id === selectedGroup.id);
      if (updatedGroup && (showEditMembersDialog || showGroupPermissionDialog)) {
        setSelectedGroup(updatedGroup);
      }
    }
  }, [groups, showEditMembersDialog, showGroupPermissionDialog]);

  // Loading skeleton
  if (loading && users.length === 0 && groups.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="IAM Support" />
        <div className="flex-1 px-8 pt-4 pb-4 overflow-hidden">
          <div className="iam-support-cards">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
          <Skeleton className="h-96 w-full mt-8" />
        </div>
      </div>
    );
  }

  // Error state
  if (error && users.length === 0 && groups.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="IAM Support" />
        <div className="flex-1 px-8 pt-4 pb-4 overflow-hidden">
          <div className="iam-support-error">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <h2>Failed to load IAM data</h2>
            <p>{error}</p>
            <Button onClick={refetchAll} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Get search placeholder and action button based on active tab
  const getSearchPlaceholder = () => {
    switch (activeTab) {
      case 'users':
        return 'Search users...';
      case 'groups':
        return 'Search groups...';
      default:
        return '';
    }
  };

  const getActionButton = () => {
    switch (activeTab) {
      case 'users':
        return (
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setSelectedUser(null);
              setShowAssignDialog(true);
            }}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 h-8 px-3"
          >
            <UserPlus className="h-4 w-4" />
            Assign Permissions
          </Button>
        );
      case 'groups':
        return (
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowGroupDialog(true)}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 h-8 px-3"
          >
            <Users className="h-4 w-4" />
            Create Group
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="IAM Support"
        renderCustomTop={
          (activeTab === 'users' || activeTab === 'groups') ? (
            <div className="flex items-center gap-3">
              {activeTab === 'users' && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={getSearchPlaceholder()}
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-10 w-80"
                  />
                </div>
              )}
              {activeTab === 'groups' && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={getSearchPlaceholder()}
                    value={groupSearchTerm}
                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                    className="pl-10 w-80"
                  />
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={refetchAll}
                disabled={loading}
                className="flex items-center gap-2 h-8 px-3"
              >
                {loading ? (
                  <SpinnerLoader className="h-4 w-4" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </Button>
              {getActionButton()}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={refetchAll}
                disabled={loading}
                className="flex items-center gap-2 h-8 px-3"
              >
                {loading ? (
                  <SpinnerLoader className="h-4 w-4" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </Button>
            </div>
          )
        }
      />

      <div className="flex-1 px-8 pt-4 pb-4 overflow-hidden flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="dashboard" className="iam-support-tabs flex-1 flex flex-col min-h-0">
          <TabsList className="grid grid-cols-3 w-fit">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="users">Users ({usersTotalItems})</TabsTrigger>
            <TabsTrigger value="groups">Groups ({groupsTotalItems})</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="iam-support-tab-content mt-0 flex-1 overflow-auto">
            <InfoCards 
              totalUsers={usersTotalItems}
              superadminCount={superadminCount}
              totalGroups={groupsTotalItems}
              totalProducts={products.length}
            />
            <ProductsSection 
              products={products}
              permissionLevels={permissionLevels}
              onAddProduct={() => setShowAddProductDialog(true)} 
            />
          </TabsContent>

          <TabsContent value="users" className="mt-4 flex-1 min-h-0">
            <UserManagement
              users={users}
              products={products}
              loading={usersLoading}
              searchTerm={userSearchTerm}
              onAssignPermission={(user) => {
                setSelectedUser(user);
                setShowAssignDialog(true);
              }}
              onPromoteToSuperadmin={(user) => {
                setSelectedUser(user);
                setShowPromoteDialog(true);
              }}
              onRemovePermission={(user, product) => {
                setPermissionToRemove({ user, product });
                setShowRemovePermissionDialog(true);
              }}
              pagination={{
                currentPage: usersPage + 1,
                totalPages: Math.max(1, usersTotalPages),
                pageSize: usersRowsPerPage,
                totalItems: usersTotalItems,
                onPageChange: (p) => setUsersPage(p - 1),
                onPageSizeChange: setUsersRowsPerPage,
                pageSizeOptions: [10, 25, 50, 100],
              }}
            />
          </TabsContent>

          <TabsContent value="groups" className="mt-4 flex-1 min-h-0">
            <GroupManagement
              groups={groups}
              users={users}
              loading={groupsLoading}
              searchTerm={groupSearchTerm}
              onCreateGroup={() => setShowGroupDialog(true)}
              onAssignPermission={(group) => {
                setSelectedGroup(group);
                setShowGroupPermissionDialog(true);
              }}
              onEditMembers={(group) => {
                setSelectedGroup(group);
                setShowEditMembersDialog(true);
              }}
              onDeleteGroup={handleDeleteGroup}
              pagination={{
                currentPage: groupsPage + 1,
                totalPages: Math.max(1, groupsTotalPages),
                pageSize: groupsRowsPerPage,
                totalItems: groupsTotalItems,
                onPageChange: (p) => setGroupsPage(p - 1),
                onPageSizeChange: setGroupsRowsPerPage,
                pageSizeOptions: [10, 25, 50, 100],
              }}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Sheets */}
      <AssignPermissionSheet
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        users={users}
        products={products}
        permissionLevels={levelNames}
        selectedUser={selectedUser}
        onAssignAll={handleAssignAllPermissions}
        isSubmitting={isSubmitting}
      />

      <CreateGroupSheet
        open={showGroupDialog}
        onOpenChange={setShowGroupDialog}
        onCreate={handleCreateGroup}
        isSubmitting={isSubmitting}
      />

      <AssignGroupPermissionSheet
        open={showGroupPermissionDialog}
        onOpenChange={setShowGroupPermissionDialog}
        group={selectedGroup}
        products={products}
        permissionLevels={levelNames}
        onSave={handleSaveGroupPermissions}
        isSubmitting={isSubmitting}
      />

      <EditGroupMembersSheet
        open={showEditMembersDialog}
        onOpenChange={setShowEditMembersDialog}
        group={selectedGroup}
        users={users}
        onSave={handleSaveGroupMembers}
        isSubmitting={isSubmitting}
      />

      <PromoteToSuperadminSheet
        open={showPromoteDialog}
        onOpenChange={setShowPromoteDialog}
        user={selectedUser}
        onPromote={handlePromoteToSuperadmin}
        isSubmitting={isSubmitting}
      />

      <RemovePermissionSheet
        open={showRemovePermissionDialog}
        onOpenChange={setShowRemovePermissionDialog}
        permissionToRemove={permissionToRemove}
        onRemove={handleRemovePermission}
        isSubmitting={isSubmitting}
      />

      <AddProductSheet
        open={showAddProductDialog}
        onOpenChange={setShowAddProductDialog}
        onAdd={handleAddProduct}
        isSubmitting={isSubmitting}
      />

      {/* Delete Group Dialog */}
      <Dialog open={showDeleteGroupDialog} onOpenChange={setShowDeleteGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this group? This action cannot be undone.
              All group memberships and permissions will be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteGroupDialog(false);
                setGroupToDelete(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteGroup}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Delete Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IAMSupport;
